<template>
  <div class="bi-sample-preview">
    <BINavigation active="performance" />
    <div class="bi-sample-preview__body">
      <!-- Local visual stand-in for BISummary (same shell + BISummarySection). -->
      <div class="bi-summary">
        <div class="__header">
          <div class="__title-container">
            <div class="__title">{{ $t('page.business-intelligence.title') }}</div>
          </div>
          <div class="bi-sample-preview__date-pill">{{ dateLabel }}</div>
        </div>
        <div class="__content">
          <BISummarySection :data="previewSummary" />
        </div>
      </div>

      <!-- Local visual stand-in for BITenders (same high-level layout). -->
      <div class="page-business-intelligence-tenders">
        <div class="__results-section">
          <span class="__count">
            <span class="number-monospace">{{ previewTenders.length }}</span>
            {{ ' ' + $tc('page.business-intelligence.results', previewTenders.length) }}
          </span>
        </div>
        <div class="__content">
          <div class="bi-sample-preview__tabs">
            <span v-for="tab in tabs" :key="tab" class="bi-sample-preview__tab" :class="{ 'is-active': tab === 'all' }">
              {{ $t(`page.business-intelligence.tabs.${tab}`) }}
            </span>
          </div>
          <table class="bi-sample-preview__table">
            <thead>
              <tr>
                <th>{{ $t('page.business-intelligence.data-table-titles.title') }}</th>
                <th>{{ $t('page.business-intelligence.data-table-titles.client') }}</th>
                <th>{{ $t('page.business-intelligence.data-table-titles.start-date') }}</th>
                <th>{{ $t('page.business-intelligence.data-table-titles.end-date') }}</th>
                <th>{{ $t('page.business-intelligence.data-table-titles.winner') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in previewTenders" :key="item.id">
                <td class="bi-sample-preview__title">{{ item.title }}</td>
                <td>{{ item.procuring_entity_name }}</td>
                <td>{{ formatShortDate(toMoment(item.start_date)) }}</td>
                <td>{{ formatShortDate(toMoment(item.end_date)) }}</td>
                <td>
                  <span v-if="item.winner" class="bi-sample-preview__winner">{{ item.winner }}</span>
                  <span v-else class="bi-sample-preview__pending">{{
                    $t('page.business-intelligence.awards.pending')
                  }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BINavigation from '@/components/page-business-intelligence/BINavigation.vue';
import BISummarySection from '@/components/page-business-intelligence/BISummarySection.vue';
import { formatShortDate, getStartOfYear, getToday, toMoment } from '@/util/date-util';

const PREVIEW_SUMMARY = Object.freeze({
  current_period: {
    start_date: '2026-01-01',
    end_date: '2026-07-27',
    bids_placed: 128,
    tenders_won: 54,
    success_rate: 0.422,
    potential_bids: 410,
    potential_bids_converted: 128,
    bid_ratio: 0.312,
    lowest: 45000,
    median: 185000,
    highest: 2400000,
    bids_won_total: 12500000,
    history: [{ amount: 120000 }, { amount: 340000 }, { amount: 890000 }, { amount: 210000 }, { amount: 1500000 }],
  },
  previous_period: {
    start_date: '2025-01-01',
    end_date: '2025-07-27',
    bids_placed: 112,
    tenders_won: 41,
    success_rate: 0.366,
    potential_bids: 395,
    potential_bids_converted: 112,
    bid_ratio: 0.284,
    lowest: 52000,
    median: 160000,
    highest: 1900000,
    bids_won_total: 9800000,
  },
});

const PREVIEW_TENDERS = Object.freeze([
  {
    id: 'preview-1',
    title: 'Réfection du pont Jacques-Cartier — phase 2',
    procuring_entity_name: 'Ministère des Transports du Québec',
    start_date: '2026-01-15',
    end_date: '2026-03-28',
    winner: 'Construction Dupont Ltée',
  },
  {
    id: 'preview-2',
    title: 'Fourniture de services d’entretien ménager — édifices gouvernementaux',
    procuring_entity_name: 'Centre de services scolaire de Montréal',
    start_date: '2026-02-01',
    end_date: '2026-04-15',
    winner: 'Services Propreté Plus',
  },
  {
    id: 'preview-3',
    title: 'Acquisition de camions à ordures — lot municipal',
    procuring_entity_name: 'Ville de Québec',
    start_date: '2026-03-10',
    end_date: '2026-05-20',
    winner: null,
  },
  {
    id: 'preview-4',
    title: 'Travaux de pavage et signalisation — arrondissement Ahuntsic',
    procuring_entity_name: 'Ville de Montréal',
    start_date: '2026-04-02',
    end_date: '2026-06-12',
    winner: 'Construction Dupont Ltée',
  },
  {
    id: 'preview-5',
    title: 'Modernisation du système de chauffage — hôpital régional',
    procuring_entity_name: 'CISSS de la Montérégie',
    start_date: '2026-05-01',
    end_date: '2026-07-01',
    winner: 'Électro-Mécanique Laval',
  },
  {
    id: 'preview-6',
    title: 'Remplacement des fenêtres — école primaire Sainte-Anne',
    procuring_entity_name: 'Centre de services scolaire des Patriotes',
    start_date: '2026-01-20',
    end_date: '2026-03-05',
    winner: 'Fenêtres Boréal Inc.',
  },
  {
    id: 'preview-7',
    title: 'Entretien des espaces verts — parcs municipaux 2026',
    procuring_entity_name: 'Ville de Laval',
    start_date: '2026-02-12',
    end_date: '2026-04-30',
    winner: 'Paysages du Québec',
  },
  {
    id: 'preview-8',
    title: 'Fourniture et installation de bornes de recharge électrique',
    procuring_entity_name: 'Hydro-Québec',
    start_date: '2026-03-01',
    end_date: '2026-05-15',
    winner: null,
  },
  {
    id: 'preview-9',
    title: 'Réfection de la toiture — bibliothèque centrale',
    procuring_entity_name: 'Ville de Sherbrooke',
    start_date: '2026-03-18',
    end_date: '2026-06-01',
    winner: 'Toitures Gagnon & Fils',
  },
  {
    id: 'preview-10',
    title: 'Services de déneigement — secteur industriel',
    procuring_entity_name: 'Ville de Trois-Rivières',
    start_date: '2026-04-08',
    end_date: '2026-06-20',
    winner: 'Déneigement Nordique',
  },
  {
    id: 'preview-11',
    title: 'Aménagement d’une piste cyclable — corridor riverain',
    procuring_entity_name: 'Ville de Gatineau',
    start_date: '2026-04-22',
    end_date: '2026-07-10',
    winner: 'Construction Dupont Ltée',
  },
  {
    id: 'preview-12',
    title: 'Acquisition de mobilier de bureau — administration',
    procuring_entity_name: 'Société québécoise des infrastructures',
    start_date: '2026-05-05',
    end_date: '2026-07-15',
    winner: 'Bureau Plus Montréal',
  },
  {
    id: 'preview-13',
    title: 'Réhabilitation des conduites d’eau potable — phase 1',
    procuring_entity_name: 'Ville de Longueuil',
    start_date: '2026-01-08',
    end_date: '2026-03-18',
    winner: 'Aqua-Tech Québec',
  },
  {
    id: 'preview-14',
    title: 'Services de sécurité incendie — caserne mobile',
    procuring_entity_name: 'MRC de L’Assomption',
    start_date: '2026-02-20',
    end_date: '2026-05-08',
    winner: null,
  },
  {
    id: 'preview-15',
    title: 'Rénovation des vestiaires — complexe sportif municipal',
    procuring_entity_name: 'Ville de Saguenay',
    start_date: '2026-03-25',
    end_date: '2026-06-28',
    winner: 'Groupe Pomerleau',
  },
  {
    id: 'preview-16',
    title: 'Fourniture de logiciels de gestion documentaire',
    procuring_entity_name: 'Commission des normes, de l’équité et de la santé',
    start_date: '2026-04-14',
    end_date: '2026-07-05',
    winner: 'Solutions Numériques ABC',
  },
  {
    id: 'preview-17',
    title: 'Travaux d’éclairage public — artères principales',
    procuring_entity_name: 'Ville de Lévis',
    start_date: '2026-05-12',
    end_date: '2026-07-22',
    winner: 'Électro-Lumière Inc.',
  },
  {
    id: 'preview-18',
    title: 'Construction d’un garage municipal — secteur est',
    procuring_entity_name: 'Municipalité de Saint-Jérôme',
    start_date: '2026-01-28',
    end_date: '2026-04-22',
    winner: 'EBC Inc.',
  },
]);

export default {
  name: 'BISamplePreview',
  components: {
    BINavigation,
    BISummarySection,
  },
  data() {
    return {
      previewSummary: PREVIEW_SUMMARY,
      previewTenders: PREVIEW_TENDERS,
      tabs: ['all', 'unawarded', 'won', 'lost', 'interested-tenderer'],
    };
  },
  computed: {
    dateLabel() {
      return `${formatShortDate(toMoment(getStartOfYear()))} – ${formatShortDate(toMoment(getToday()))}`;
    },
  },
  methods: {
    formatShortDate,
    toMoment,
  },
};
</script>

<style lang="scss">
.bi-sample-preview {
  display: flex;
  flex-direction: column;
  height: calc(var(--viewport-height) - #{$menu-top-bar-height});
  max-height: calc(var(--viewport-height) - #{$menu-top-bar-height});
  min-height: 0;
  overflow: hidden;
  background: $color-slate-50;

  &__body {
    flex: 1;
    min-height: 0;
    overflow: hidden;
    padding: 24px 32px;
  }

  .bi-navigation {
    position: relative;
    top: 0;
    flex-shrink: 0;
  }

  // Soften nested BI blocks so they fit the clipped preview viewport.
  .bi-summary {
    margin-bottom: 16px;
    padding: 16px 20px;

    > .__content {
      margin-top: 16px;
    }
  }

  .page-business-intelligence-tenders {
    > .__results-section {
      margin-bottom: 8px;
    }
  }

  &__date-pill {
    padding: 6px 12px;
    border-radius: $radius-md;
    background: $color-slate-100;
    color: $color-slate-700;
    font-size: 13px;
    font-weight: $font-weight-medium;
  }

  &__tabs {
    display: flex;
    gap: 4px;
    border-bottom: 1px solid $color-border;
    margin-bottom: 12px;
  }

  &__tab {
    padding: 10px 14px;
    font-size: 13px;
    color: $color-slate-500;
    border-bottom: 2px solid transparent;

    &.is-active {
      color: $color-accent-500;
      border-bottom-color: $color-accent-500;
      font-weight: $font-weight-bold;
    }
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
    background: $color-white;
    border-radius: $border-radius-root;
    overflow: hidden;

    th,
    td {
      padding: 10px 12px;
      text-align: left;
      font-size: 13px;
      border-bottom: 1px solid $color-slate-100;
      vertical-align: top;
    }

    th {
      color: $color-slate-500;
      font-weight: $font-weight-bold;
      background: $color-slate-50;
      white-space: nowrap;
    }

    td {
      color: $color-slate-800;
    }
  }

  &__title {
    font-weight: $font-weight-medium;
    max-width: 320px;
  }

  &__winner {
    display: inline-block;
    padding: 2px 8px;
    border-radius: $radius-full;
    background: rgba($color-accent-500, 0.12);
    color: $color-accent-700;
    font-size: 12px;
    font-weight: $font-weight-bold;
  }

  &__pending {
    color: $color-slate-400;
    font-size: 12px;
    font-style: italic;
  }
}
</style>
